import { Component, Input, Output, EventEmitter, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ScreensizeStateService } from '../../../../state-management/services/screensize-state.service';
import { CoordinatesSystem, ScreenWidthBreakpoints } from '../../../../state-management/model/enums/screen-size.enums';
import { LoggerService } from '../../../../services/logger.service';

@Component({
  selector: 'app-discount-banner',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './discount-banner.component.html',
  styleUrls: ['./discount-banner.component.scss'],
  animations: [
    trigger('slideDown', [
      state('hidden', style({
        transform: 'translateY(-100%)',
        opacity: 0
      })),
      state('visible', style({
        transform: 'translateY(0)',
        opacity: 1
      })),
      transition('hidden => visible', animate('300ms ease-out')),
      transition('visible => hidden', animate('300ms ease-in'))
    ])
  ]
})
export class DiscountBannerComponent {
  @Input() code: string = '';
  @Input() discountText: string = '';
  @Input() expiresAt: Date | null = null;
  @Input() isVisible: boolean = false;
  
  @Output() copyCode = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();

  constructor(
    private logger: LoggerService,
    private screensizeState: ScreensizeStateService) {}

  public isSmallScreen$: Signal<boolean> = this.screensizeState.isScreenSizes(CoordinatesSystem.ScreenWidth, [ScreenWidthBreakpoints.XSmall, ScreenWidthBreakpoints.Small]);

  onCopyCode(): void {
    if (this.code) {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(this.code).then(() => {
          this.logger.log('✅ Code copied to clipboard (modern API):', this.code);
          this.copyCode.emit(this.code);
        }).catch(err => {
          this.logger.error('Modern clipboard API failed, trying fallback:', err);
          this.fallbackCopyTextToClipboard(this.code);
        });
      } else {
        // Fallback for older browsers or non-secure contexts
        this.fallbackCopyTextToClipboard(this.code);
      }
    }
  }

  private fallbackCopyTextToClipboard(text: string): void {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Make it invisible but still focusable
    textArea.style.position = 'fixed';
    textArea.style.top = '-1000px';
    textArea.style.left = '-1000px';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    
    // Add to DOM, select, copy, and remove
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.logger.log('✅ Code copied to clipboard (fallback):', text);
        this.copyCode.emit(text);
      } else {
        this.logger.error('❌ Fallback copy failed');
        this.handleCopyFailure();
      }
    } catch (err) {
      this.logger.error('❌ Fallback copy error:', err);
      this.handleCopyFailure();
    } finally {
      document.body.removeChild(textArea);
    }
  }

  private handleCopyFailure(): void {
    // Show user-friendly message or alternative action
    if (confirm(`Code konnte nicht automatisch kopiert werden.\n\nCode: ${this.code}\n\nManuell kopieren?`)) {
      // User can manually copy from the alert/prompt
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
      }
    }
  }

  onClose(): void {
    this.close.emit();
  }
}
